import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField, required } from '@angular/forms/signals';
import { SignalCheckbox, SignalDateField, SignalDropdown, SignalFormDirective, SignalTextField } from '@vallift/ngl';
import type { DateFormat, FormField } from '../../../../models/built-form';

interface SelectOption<T> {
  label: string;
  value: T;
}

interface DateConfigModel {
  label: string;
  helpText: string;
  minDate: Date | null;
  maxDate: Date | null;
  disablePast: boolean;
  disableFuture: boolean;
  defaultDate: Date | null;
  dateFormat: DateFormat;
  required: boolean;
  customMessage: string;
}

@Component({
  selector: 'app-date-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalTextField,
    SignalCheckbox,
    SignalDropdown,
    SignalDateField,
    SignalFormDirective,
  ],
  templateUrl: './date-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();
  readonly dateFormatOptions: SelectOption<DateFormat>[] = [
    {
      label: 'DD/MM/YYYY',
      value: 'DD/MM/YYYY',
    },
    {
      label: 'MM/DD/YYYY',
      value: 'MM/DD/YYYY',
    },
  ];

  readonly model = signal<DateConfigModel>({
    label: '',
    helpText: '',
    minDate: null,
    maxDate: null,
    disablePast: false,
    disableFuture: false,
    defaultDate: null,
    dateFormat: 'DD/MM/YYYY',
    required: false,
    customMessage: '',
  });

  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
  });
  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();

      this.lastFieldId = next.id;
      this.suppressEmit = true;

      const validation = next.validation ?? {};

      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        minDate: next.dateMin ?? null,
        maxDate: next.dateMax ?? null,
        disablePast: next.dateDisablePast ?? false,
        disableFuture: next.dateDisableFuture ?? false,
        defaultDate: next.dateDefault ?? null,
        dateFormat: next.dateFormat ?? 'DD/MM/YYYY',
        required: next.required ?? validation.required ?? false,
        customMessage: validation.customMessage ?? '',
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label.trim(),
        helpText: draft.helpText.trim() || undefined,
        required: draft.required,
        dateMin: draft.minDate || undefined,
        dateMax: draft.maxDate || undefined,
        dateDisablePast: draft.disablePast,
        dateDisableFuture: draft.disableFuture,
        dateDefault: draft.defaultDate || undefined,
        dateFormat: draft.dateFormat,
        validation: {
          required: draft.required,
          customMessage: draft.customMessage.trim() || undefined,
        },
        options: undefined,
        headingLevel: undefined,
        spacerSize: undefined,
      });
    });
  }
}
